import { useEffect, useId, useRef, useState } from 'react';
import { FaBell, FaTimes } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useBillingNotificationSession } from '../../context/BillingNotificationSession';
import { logoutUser } from '../../redux/actions';
import { getBillingNotificationTarget } from '../../utils/billingNotificationApi';
import './index.scss';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const EVENT_LABELS = {
  created: 'Invoice created',
  first_sent: 'Invoice sent',
  paid: 'Invoice paid',
  cancelled: 'Invoice cancelled',
  send_failed: 'Invoice delivery failed',
  overdue: 'Invoice overdue',
};

const EVENT_STATUS = {
  created: 'Draft',
  first_sent: 'Sent',
  paid: 'Paid',
  cancelled: 'Cancelled',
  overdue: 'Overdue',
};

const IMPORTANT_EVENTS = new Set(['send_failed', 'cancelled', 'overdue']);

const invoiceLabel = (notification) =>
  `INV-${String(notification.invoiceNumber ?? notification.invoiceId).padStart(5, '0')}`;

const formatDate = (date) => {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const BillingNotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    status,
    session,
    activePastDue,
    notifications,
    source,
    error,
    errorKind,
    retry,
    refreshIfStale,
  } = useBillingNotificationSession();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const panelId = useId();
  const headingId = useId();
  const visiblePastDue = session && status === 'ready' ? activePastDue || [] : [];
  const visibleNotifications = session && status === 'ready' ? notifications || [] : [];
  const count = visiblePastDue.length;

  const togglePanel = () => {
    if (!open) refreshIfStale?.();
    setOpen((current) => !current);
  };

  useEffect(() => {
    if (!open) return undefined;
    dialogRef.current?.focus();
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const selectNotification = (notification) => {
    const target = getBillingNotificationTarget(notification);
    if (!target) return;
    setOpen(false);
    navigate(target);
  };

  const signInAgain = () => {
    dispatch(logoutUser());
    setOpen(false);
    navigate('/login');
  };

  return (
    <div className="billing-notifications" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="header-icon-btn billing-notifications__trigger"
        aria-label={
          count === 0
            ? 'Billing notifications'
            : `Billing notifications, ${count} ${count === 1 ? 'past-due invoice' : 'past-due invoices'}`
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={togglePanel}>
        <FaBell size={18} aria-hidden="true" />
        {count > 0 && (
          <span className="billing-notifications__badge" aria-hidden="true">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <section
          ref={dialogRef}
          id={panelId}
          className="billing-notifications__panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby={headingId}
          tabIndex={-1}>
          <div className="billing-notifications__header">
            <h2 id={headingId}>Billing notifications</h2>
            <div className="billing-notifications__actions">
              <button
                type="button"
                className="billing-notifications__refresh"
                onClick={retry}
                disabled={status === 'loading'}>
                Refresh
              </button>
              <button
                type="button"
                className="billing-notifications__close"
                aria-label="Close billing notifications"
                onClick={() => setOpen(false)}>
                <FaTimes aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="billing-notifications__body" aria-live="polite">
            {status === 'idle' && (
              <p className="billing-notifications__state">
                Billing notifications are unavailable for this session.
              </p>
            )}
            {status === 'loading' && (
              <p className="billing-notifications__state">Loading notifications…</p>
            )}
            {status === 'error' && (
              <div className="billing-notifications__state">
                <p>{error || 'Unable to load billing notifications.'}</p>
                {errorKind === 'authentication' ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={signInAgain}>
                    Sign in again
                  </button>
                ) : errorKind !== 'authorization' ? (
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={retry}>
                    Try again
                  </button>
                ) : null}
              </div>
            )}
            {status === 'ready' && (
              <>
                <section className="billing-notifications__section" aria-label="Past-due invoices">
                  <h3>Past-due invoices ({count})</h3>
                  {source === 'invoice-status' && (
                    <p className="billing-notifications__state">
                      Unpaid sent invoices with a due date before today (UTC). Invoice records are
                      not changed.
                    </p>
                  )}
                  {count === 0 ? (
                    <p className="billing-notifications__state">No currently past-due invoices.</p>
                  ) : (
                    <div className="billing-notifications__list">
                      {visiblePastDue.map((invoice) => (
                        <button
                          type="button"
                          className="billing-notifications__item billing-notifications__item--important"
                          key={invoice.invoiceId}
                          onClick={() => selectNotification(invoice)}>
                          <span className="billing-notifications__item-top">
                            <strong>{invoiceLabel(invoice)}</strong>
                            <strong>{currency.format(invoice.totalAmount)}</strong>
                          </span>
                          <span className="billing-notifications__client">
                            {invoice.clientName}
                          </span>
                          <span className="billing-notifications__event">Past due · Important</span>
                          <span>Current status: {invoice.invoiceStatus}</span>
                          <span>Due {formatDate(invoice.dueDate)}</span>
                          <span
                            className={
                              !invoice.contactEmail ? 'billing-notifications__missing' : undefined
                            }>
                            {invoice.contactEmail || 'Billing contact missing'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
                {source !== 'invoice-status' && (
                  <section className="billing-notifications__section" aria-label="Recent activity">
                    <h3>Recent activity</h3>
                    {visibleNotifications.length === 0 ? (
                      <p className="billing-notifications__state">No recent invoice activity.</p>
                    ) : (
                      <div className="billing-notifications__list">
                        {visibleNotifications.map((notification) => (
                          <button
                            type="button"
                            className={`billing-notifications__item billing-notifications__item--${IMPORTANT_EVENTS.has(notification.eventType) ? 'important' : 'soft'}`}
                            key={notification.id}
                            onClick={() => selectNotification(notification)}>
                            <span className="billing-notifications__item-top">
                              <strong>{invoiceLabel(notification)}</strong>
                              <strong>{currency.format(Number(notification.totalAmount))}</strong>
                            </span>
                            <span className="billing-notifications__event">
                              {EVENT_LABELS[notification.eventType] || notification.eventType}
                            </span>
                            <span className="billing-notifications__severity">
                              {IMPORTANT_EVENTS.has(notification.eventType) ? 'Important' : 'Soft'}
                            </span>
                            {EVENT_STATUS[notification.eventType] && (
                              <span>Status at event: {EVENT_STATUS[notification.eventType]}</span>
                            )}
                            <span>Current status: {notification.invoiceStatus}</span>
                            <span className="billing-notifications__client">
                              {notification.clientName}
                            </span>
                            <span>
                              Event{' '}
                              <time dateTime={notification.eventTime}>
                                {notification.eventTime}
                              </time>
                            </span>
                            <span>Due {formatDate(notification.dueDate)}</span>
                            <span
                              className={
                                !notification.contactEmail
                                  ? 'billing-notifications__missing'
                                  : undefined
                              }>
                              {notification.contactEmail || 'Billing contact missing'}
                            </span>
                            {notification.eventType === 'send_failed' && (
                              <>
                                <span className="billing-notifications__failure">
                                  {notification.failureSummary}
                                </span>
                                <span>
                                  Open the invoice workflow to correct the billing contact or retry.
                                </span>
                              </>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default BillingNotificationBell;
